import { useEffect, useMemo, useRef, useState } from 'react'
import { ClientApplication, ClientUser } from '@numeo/types-core'
import { Alert, AlertDialog, Button, Modal, TextField } from '@/components/ds2'
import { useAuth } from 'hooks/auth'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { TiptapEditor, TiptapEditorRef } from '@/components/tiptap/TiptapEditor'
import { migrateTemplateHtml, serializeTemplateHtml } from '@/components/tiptap/utils'
import { saveEmailTemplate } from '@/pages/dashboard/pages/settings/api/EmailSettings'
import { getCurrentEmailConfig } from 'features/load-hub/hooks/usePrivateLoadEmail'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { ensureHtmlLineBreaks } from 'features/load-hub/utils/emailTemplateUtils'
import { ComposeEmailType, TYPE_TO_TEMPLATE_TYPE } from 'features/load-hub/components/expanded-row/savedTemplate'
import { BOX_TRUCK_DEFAULT_TEMPLATES } from '../constants/emailTemplates'
import { findUnknownTemplateVariables } from '../constants/emailTemplateVariables'
import { DESIGN_EDITOR_ENABLED, TEMPLATE_EDITOR_MODES, type TemplateEditorMode } from '../constants/templateEditor'
import { findVisualEditorUnsupportedElements, inspectTemplateHtml, sanitizeTemplateHtml } from '../utils/sanitizeTemplateHtml'
import { applyEmailBackgroundColor, readEmailBackgroundColor } from '../utils/templateAppearance'
import { useTemplateDesignPaste } from '../hooks/useTemplateDesignPaste'
import { buildVariableDisplayValues, renderTemplatePreview } from '../utils/templatePreview'
import { TemplateDesignCode } from './TemplateDesignCode'
import { TemplateDesignControls } from './TemplateDesignControls'
import { TemplateEditorModeBar } from './TemplateEditorModeBar'
import { TemplatePreview } from './TemplatePreview'
import { TemplateSimpleAppearanceControls } from './TemplateSimpleAppearanceControls'
import { TemplateVariablePicker } from './TemplateVariablePicker'

interface TemplateEditModalProps {
    emailType: ComposeEmailType
    previewValues: Readonly<Record<string, string>>
    onClose: () => void
    onSaved: () => void
}

const TITLES: Record<ComposeEmailType, string> = { info: 'Info', offer: 'Offer', book: 'Book' }
const MOBILE_QUERY = '(max-width: 899px)'
const isEditorMode = (mode: string): mode is TemplateEditorMode => mode === 'visual' || mode === 'html' || mode === 'design' || mode === 'preview'

/** Box Truck Info/Offer/Book editor; signature and CC are preserved verbatim. */
export const TemplateEditModal = ({ emailType, previewValues, onClose, onSaved }: TemplateEditModalProps) => {
    const isMobile = useMediaQuery(MOBILE_QUERY)
    const { user, setUser, setApplication } = useAuth()
    const { showSnackbar } = useSnackbar()
    const bodyRef = useRef<TiptapEditorRef>(null)
    const focusedRef = useRef<'subject' | 'body'>('body')
    const [saving, setSaving] = useState(false)
    const templateType = TYPE_TO_TEMPLATE_TYPE[emailType]

    const raw = useMemo(() => {
        const config = getCurrentEmailConfig()
        const nylasEmail =
            (config?.selectedEmail && user?.nylasEmails?.find((email) => email.email === config.selectedEmail)) ||
            user?.nylasEmails?.find((email) => email.isPrimary) ||
            user?.nylasEmails?.[0]
        const templates = nylasEmail?.emailTemplates?.bodyTemplates || user?.emailTemplates?.bodyTemplates || []
        return templates.find((template) => template.type === templateType && template.body && template.subject) ?? BOX_TRUCK_DEFAULT_TEMPLATES[emailType]
    }, [user, templateType, emailType])

    const initialSource = ensureHtmlLineBreaks(raw.body)
    const initialInspection = useMemo(() => inspectTemplateHtml(initialSource), [initialSource])
    const safeInitialBody = initialInspection.sanitizedHtml || sanitizeTemplateHtml(ensureHtmlLineBreaks(BOX_TRUCK_DEFAULT_TEMPLATES[emailType].body))
    const initialUnsupportedElements = useMemo(() => findVisualEditorUnsupportedElements(safeInitialBody), [safeInitialBody])
    // A template the Visual editor cannot hold (column definitions) or that has issues opens on its preview instead.
    const startsOutsideVisual = initialInspection.issues.length > 0 || initialUnsupportedElements.length > 0
    const [subject, setSubject] = useState(raw.subject)
    const [bodyHtml, setBodyHtml] = useState(() => migrateTemplateHtml(safeInitialBody))
    const [htmlSource, setHtmlSource] = useState(startsOutsideVisual ? initialSource : safeInitialBody)
    const [editorMode, setEditorMode] = useState<TemplateEditorMode>(startsOutsideVisual ? 'preview' : 'visual')
    // Read by the visual editor's change handler, which can fire before a re-render hands it a fresh closure.
    const editorModeRef = useRef(editorMode)
    useEffect(() => {
        editorModeRef.current = editorMode
    }, [editorMode])
    const [advancedOpen, setAdvancedOpen] = useState(startsOutsideVisual)
    const preservedSignature = raw.signature ?? ''
    const preservedCc = 'cc' in raw ? (raw.cc ?? []) : []

    const inspection = useMemo(() => inspectTemplateHtml(htmlSource), [htmlSource])
    const unknownVariables = useMemo(() => findUnknownTemplateVariables(subject, htmlSource), [subject, htmlSource])
    const blockingIssues = useMemo(
        () => [...inspection.issues, ...unknownVariables.map((variable) => `Unknown template variable {{${variable}}}`)],
        [inspection.issues, unknownVariables]
    )
    const displayValues = useMemo(() => buildVariableDisplayValues(previewValues), [previewValues])
    const editorModes = useMemo(() => TEMPLATE_EDITOR_MODES.filter((mode) => DESIGN_EDITOR_ENABLED || mode.key !== 'design'), [])
    const previewSubject = useMemo(() => renderTemplatePreview(subject, displayValues, false), [subject, displayValues])
    const previewHtml = useMemo(
        () => sanitizeTemplateHtml(renderTemplatePreview(inspection.sanitizedHtml, previewValues, true)),
        [inspection.sanitizedHtml, previewValues]
    )
    const emailBackground = useMemo(() => readEmailBackgroundColor(inspection.sanitizedHtml), [inspection.sanitizedHtml])
    const hasBlockingIssues = blockingIssues.length > 0

    const currentVisualSource = (): string => serializeTemplateHtml(bodyRef.current?.editor?.getHTML() ?? bodyHtml)
    const issuesFor = (source: string): string[] => {
        const htmlIssues = inspectTemplateHtml(source).issues
        return [...htmlIssues, ...findUnknownTemplateVariables(subject, source).map((variable) => `Unknown template variable {{${variable}}}`)]
    }

    const insertVariable = (key: string) => {
        // Outside Visual the body is a design only Edit with AI changes, so a variable can only go to the subject.
        if (focusedRef.current !== 'body' || editorMode !== 'visual') {
            setSubject((current) => `${current}{{${key}}}`)
            return
        }
        bodyRef.current?.insertVariable(key)
    }

    // A checked design from any paste path: shown on Preview, and loaded into Visual too when Visual can hold it.
    const showDesign = (safeDesign: string) => {
        setHtmlSource(safeDesign)
        if (findVisualEditorUnsupportedElements(safeDesign).length === 0) {
            setBodyHtml(migrateTemplateHtml(safeDesign))
            bodyRef.current?.editor?.commands.setContent(migrateTemplateHtml(safeDesign), false)
        }
        setAdvancedOpen(true)
        setEditorMode('preview')
    }
    const designPaste = useTemplateDesignPaste({ issuesFor, onApply: showDesign })

    const handleEditorChange = (html: string) => {
        // The visual editor stays mounted while hidden, and it cannot hold every template — it has no column
        // definitions. Outside Visual mode its updates (normalising the seeded HTML as it mounts, for one) would replace
        // the source the operator is editing, previewing and saving with that reshaped copy.
        if (editorModeRef.current !== 'visual') return
        setBodyHtml(html)
        setHtmlSource(serializeTemplateHtml(html))
    }

    const handleStructuredHtmlChange = (html: string) => {
        const sanitized = inspectTemplateHtml(html).sanitizedHtml
        setHtmlSource(sanitized)
        setBodyHtml(migrateTemplateHtml(sanitized))
        bodyRef.current?.editor?.commands.setContent(migrateTemplateHtml(sanitized), false)
    }

    const changeEditorMode = (mode: string): boolean => {
        if (!isEditorMode(mode)) return false
        const source = editorMode === 'visual' ? currentVisualSource() : htmlSource
        const sourceIssues = issuesFor(source)
        // Preview renders a sanitized copy, so it stays open to a design with issues — it is how the operator sees one.
        if (mode === 'visual' && sourceIssues.length > 0) {
            showSnackbar(SnackbarType.ERROR, 'This design has problems that block saving. Paste a fixed design, or click Reset to default.')
            return false
        }
        const safeSource = inspectTemplateHtml(source).sanitizedHtml
        const unsupportedVisualElements = mode === 'visual' ? findVisualEditorUnsupportedElements(safeSource) : []
        if (unsupportedVisualElements.length > 0) {
            showSnackbar(SnackbarType.ERROR, 'This design’s layout can’t be changed in the Visual tab. To change it, use Edit with AI.')
            return false
        }
        setHtmlSource(sourceIssues.length > 0 ? source : safeSource)
        if (mode === 'visual') setBodyHtml(migrateTemplateHtml(safeSource))
        setEditorMode(mode)
        return true
    }

    const toggleAdvanced = () => {
        setAdvancedOpen((open) => !open)
    }

    const handleEmailBackgroundChange = (backgroundColor: string | null) => {
        handleStructuredHtmlChange(applyEmailBackgroundColor(currentVisualSource(), backgroundColor))
    }

    const handleTextColorChange = (color: string | null) => {
        const chain = bodyRef.current?.editor?.chain().focus()
        if (color) chain?.setColor(color).run()
        else chain?.unsetColor().run()
    }

    const handleTextBackgroundChange = (color: string | null) => {
        const chain = bodyRef.current?.editor?.chain().focus()
        if (color) chain?.setTextBackgroundColor(color).run()
        else chain?.unsetTextBackgroundColor().run()
    }

    const resetToDefault = () => {
        const defaultTemplate = BOX_TRUCK_DEFAULT_TEMPLATES[emailType]
        const defaultBody = sanitizeTemplateHtml(ensureHtmlLineBreaks(defaultTemplate.body))
        setSubject(defaultTemplate.subject)
        setHtmlSource(defaultBody)
        setBodyHtml(migrateTemplateHtml(defaultBody))
        bodyRef.current?.editor?.commands.setContent(migrateTemplateHtml(defaultBody), false)
        setEditorMode('visual')
    }

    const handleSave = async () => {
        const source = editorMode === 'visual' ? currentVisualSource() : htmlSource
        const saveIssues = issuesFor(source)
        if (saveIssues.length > 0) {
            showSnackbar(SnackbarType.ERROR, 'Fix all template issues before saving')
            return
        }
        const normalizedBody = inspectTemplateHtml(source).sanitizedHtml
        if (!subject.trim() || !normalizedBody.trim()) {
            showSnackbar(SnackbarType.ERROR, 'Subject and body are required')
            return
        }
        setSaving(true)
        try {
            const response = await saveEmailTemplate({
                type: templateType,
                subject: subject.trim(),
                body: normalizedBody,
                signature: preservedSignature,
                emailFooter: preservedSignature,
                cc: preservedCc,
            })
            if (response?.data?.user) setUser(response.data.user as ClientUser)
            if (response?.data?.application) setApplication(response.data.application as ClientApplication)
            showSnackbar(SnackbarType.SUCCESS, `${TITLES[emailType]} template saved`)
            onSaved()
            onClose()
        } catch (error) {
            showSnackbar(SnackbarType.ERROR, error instanceof Error ? error.message : 'Failed to save template')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            open
            onClose={onClose}
            size={isMobile ? 'full' : 'lg'}
            header={<span className="text-sm font-semibold text-foreground">Edit {TITLES[emailType]} email template</span>}
            footer={
                <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || hasBlockingIssues || !subject.trim() || !htmlSource.trim()}>
                        {saving ? 'Saving…' : 'Save template'}
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-3">
                <TextField
                    label="Subject"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    onFocus={() => (focusedRef.current = 'subject')}
                    placeholder="Subject…"
                />

                <TemplateEditorModeBar
                    advancedOpen={advancedOpen}
                    editorMode={editorMode}
                    editorModes={editorModes}
                    subject={subject}
                    html={htmlSource}
                    onModeChange={(mode) => void changeEditorMode(mode)}
                    onToggleAdvanced={toggleAdvanced}
                    onUndo={() => bodyRef.current?.editor?.chain().focus().undo().run()}
                    onRedo={() => bodyRef.current?.editor?.chain().focus().redo().run()}
                    onReset={resetToDefault}
                    onPasteAiResult={designPaste.pasteAiResult}
                />

                {hasBlockingIssues && (
                    <Alert
                        variant="danger"
                        title="This template cannot be saved"
                        description={`${blockingIssues.slice(0, 3).join(' · ')}${blockingIssues.length > 3 ? ` · ${blockingIssues.length - 3} more` : ''}`}
                    />
                )}

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-foreground-muted">
                        {editorMode === 'visual' ? 'Body' : editorMode === 'html' ? 'Design code' : editorMode === 'design' ? 'Design' : 'Rendered email'}
                    </span>
                    <div
                        className={editorMode === 'visual' ? 'block' : 'hidden'}
                        onFocusCapture={() => (focusedRef.current = 'body')}
                        onPasteCapture={designPaste.handleVisualPaste}
                    >
                        <TiptapEditor
                            ref={bodyRef}
                            content={bodyHtml}
                            onChange={handleEditorChange}
                            placeholder="Email body…"
                            minHeight={160}
                            preserveHtml
                            htmlPreviewRoleResolver={BOX_TRUCK_DEFAULT_TEMPLATES[emailType].previewRoleResolver}
                        />
                    </div>
                    {advancedOpen && editorMode === 'html' && <TemplateDesignCode html={htmlSource} onPaste={designPaste.pasteDesignCode} />}
                    {advancedOpen && DESIGN_EDITOR_ENABLED && editorMode === 'design' && (
                        <div className="flex flex-col gap-3">
                            <TemplateDesignControls html={inspection.sanitizedHtml} onChange={handleStructuredHtmlChange} />
                            <TemplatePreview subject={subject} html={inspection.sanitizedHtml} />
                        </div>
                    )}
                    {advancedOpen && editorMode === 'preview' && <TemplatePreview subject={previewSubject} html={previewHtml} />}
                    {!advancedOpen && editorMode !== 'visual' && <TemplatePreview subject={previewSubject} html={previewHtml} />}
                </div>

                {editorMode === 'visual' && (
                    <TemplateSimpleAppearanceControls
                        emailBackground={emailBackground}
                        onTextColorChange={handleTextColorChange}
                        onTextBackgroundChange={handleTextBackgroundChange}
                        onEmailBackgroundChange={handleEmailBackgroundChange}
                    />
                )}

                {editorMode !== 'design' && (
                    <TemplateVariablePicker
                        values={displayValues}
                        onInsert={insertVariable}
                        hint={editorMode === 'visual' ? undefined : 'Adds the variable to the subject. To change the design, use Edit with AI.'}
                    />
                )}
            </div>
            <AlertDialog
                open={designPaste.replaceConfirmOpen}
                onClose={designPaste.closeReplace}
                title="Replace your template with the pasted design?"
                description="Your current template will be replaced and you will see a preview. Nothing is saved until you click Save template."
                confirmLabel="Replace design"
                cancelLabel="Cancel"
                variant="accent"
                onConfirm={designPaste.confirmReplace}
            />
        </Modal>
    )
}
